
import { namespace } from 'vuex-class';
import { Vue, Component } from 'vue-property-decorator';

import SingleStepTeam from '@/pages/sports/single-step/SingleStepTeam.vue';
import SingleStepCompetition from '@/pages/sports/single-step/SingleStepCompetition.vue';

import {
	GetSingleStepTeamOrCompetitionDocument,
	GetSingleStepTeamOrCompetitionQuery,
	GetSingleStepTeamOrCompetitionQueryVariables,
} from '@/pages/graphql/queries/GetSingleStepTeamOrCompetition.query';
import { Platform } from '@/@types/graphql-types';

import { LocaleResponse } from '@/helpers/locale-helper';
import { shiftDateWithTimezone } from '@/helpers/date-helper';

const Language = namespace('language');

@Component({
	name: 'SingleStepTeamOrCompetition',
	components: {
		SingleStepTeam,
		SingleStepCompetition,
	},
	apollo: {
		teamOrCompetitionData: {
			query: GetSingleStepTeamOrCompetitionDocument,
			variables(): GetSingleStepTeamOrCompetitionQueryVariables {
				return this.queryVariables;
			},
			update(data: GetSingleStepTeamOrCompetitionQuery) {
				return data.urlV2;
			},
			skip() {
				return !((this.$route.meta?.data as string) ?? '').includes('singlestep');
			},
		},
	},
})
export default class SingleStepTeamOrCompetition extends Vue {
	teamOrCompetitionData: GetSingleStepTeamOrCompetitionQuery['urlV2'];

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;

	/*
		Data is fetched here but not passed down to children on purpose.
		
		The queries used for this page and both children all use the same fragments. The
		idea is that Apollo will then be able to cache everything appropriately so that
		when the correct child loads (team or competition page) it can take over the data
		intelligently without making another request.

		The main difference is competition pages dont have a `filter.startAt.to` field so
		they normally fetch more data. I prefered to opt for less data fetched.
	*/

	get queryVariables(): GetSingleStepTeamOrCompetitionQueryVariables {
		return {
			fullPath: this.$route.path,
			language: this.language,
			country: this.country,
			platform: Platform.Web,
			first: 20, // matches "first" in competition pages; number of events to get,
			timezoneOffset: 0,
			filter: {
				startAt: {
					from: shiftDateWithTimezone(this.localeObject.timezone).toISOString(),
				},
				packages: [],
				competitionIds: [],
			},
		};
	}

	get pageObjectType() {
		if (
			this.teamOrCompetitionData?.node.__typename === 'SportCompetitorV2' ||
			this.teamOrCompetitionData?.node.__typename === 'SportCompetitionV2'
		) {
			return this.teamOrCompetitionData.node.objectType;
		}
	}
}
